<script>
import { mapState } from "vuex";
import { format } from "date-fns";
import { CERTIFICATES_STATUSES } from "@/package/const/certificates-statuses";
import MainButton from "@/components/helpers/MainButton.vue";
import MainButtonIcon from "@/package/components/MainButtonIcon.vue";
import CertificatesShare from "@/components/certificates/CertificatesShare.vue";

export default {
  name: "CertificatesItem",

  components: { CertificatesShare, MainButtonIcon, MainButton },

  props: {
    certificate: {
      type: Object,
      default: () => ({}),
    },
  },

  data() {
    return {
      isShowPopperShare: false,
      isShowPopperShareMobile: false,
    };
  },

  computed: {
    ...mapState({
      innerWidth: (state) => state.innerWidth,
    }),

    CERTIFICATES_STATUSES() {
      return CERTIFICATES_STATUSES;
    },
    nominal() {
      return `${Intl.NumberFormat("ru-RU").format(this.certificate.amount)} RC`;
    },

    createdDate() {
      return format(new Date(this.certificate.created_at), "dd.MM.yyyy");
    },

    activatedDate() {
      return format(new Date(this.certificate.activated_at), "dd.MM.yyyy");
    },
  },

  methods: {
    openCertificate() {
      window.open(
        `${process.env.VUE_APP_FRONT_URL}certificate/${this.certificate.id}`
      );
    },

    share() {
      if (navigator.share) {
        navigator.share({
          url: `${process.env.VUE_APP_FRONT_URL}certificate/${this.certificate.id}`,
          text: "Подарочный сертификат",
          title: "Подарочный сертификат",
        });

        return;
      }

      if (this.innerWidth >= 900) {
        this.isShowPopperShare = true;
      } else {
        this.isShowPopperShareMobile = true;
      }
    },
  },
};
</script>

<template>
  <div
    class="certificates-item"
    :class="`certificates-item_${certificate.status}`"
  >
    <div class="certificates-item__info info">
      <p class="info__number">№{{ certificate.number }}</p>

      <div class="info__title">
        <h6>{{ nominal }}</h6>
        <span class="info__status">{{
          CERTIFICATES_STATUSES[certificate.status].certificateLabel
        }}</span>
      </div>

      <p class="info__date">Дата покупки: {{ createdDate }}</p>

      <p v-if="certificate.status === 'activated'" class="info__date">
        Дата использования: {{ activatedDate }}
      </p>
    </div>

    <div class="certificates-item__buttons buttons">
      <div class="buttons__desktop">
        <MainButton
          :title="'Открыть'"
          :color="'outlined-grey'"
          :padding="'6px 16px'"
          @click="openCertificate"
        >
          <span class="icon-link-open" />
        </MainButton>

        <VDropdown
          v-if="certificate.status === 'new'"
          popper-class="certificates-item__dropdown"
          :triggers="[]"
          :distance="12"
          :shown.sync="isShowPopperShare"
        >
          <MainButton
            :title="'Поделиться'"
            color="outlined-grey"
            :padding="'6px 16px'"
            @click="share"
          >
            <span class="icon-share" />
          </MainButton>

          <template v-if="isShowPopperShare" #popper>
            <div class="certificates-item__popper">
              <CertificatesShare
                :certificate-id="certificate.id"
                @close="isShowPopperShare = false"
              />
            </div>
          </template>
        </VDropdown>
      </div>

      <div class="buttons__mobile">
        <MainButtonIcon @click="openCertificate">
          <span class="icon-link-open" />
        </MainButtonIcon>

        <VDropdown
          v-if="certificate.status === 'new'"
          popper-class="certificates-item__dropdown"
          :triggers="[]"
          :distance="12"
          :placement="'auto'"
          :shown.sync="isShowPopperShareMobile"
        >
          <MainButtonIcon @click="share">
            <span class="icon-share" />
          </MainButtonIcon>

          <template #popper>
            <div class="certificates-item__popper">
              <CertificatesShare
                :certificate-id="certificate.id"
                @close="isShowPopperShareMobile = false"
              />
            </div>
          </template>
        </VDropdown>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.certificates-item {
  padding: 16px 20px;
  border-radius: 8px;
  background-color: $light-primary;
  display: flex;
  justify-content: space-between;
  align-items: flex-start;

  .info {
    &__number {
      @include text-2;
      color: $dark-second;
      margin-bottom: 8px;
    }

    &__title {
      display: flex;
      align-items: center;
      margin-bottom: 12px;

      > h6 {
        @include body-1-bold;
        color: $dark-primary;
      }
    }

    &__status {
      padding: 4px 8px;
      @include caption-1;
      color: $dark-primary;
      border-radius: 8px;
      margin-left: 12px;
    }

    &__date {
      @include caption-1;
      color: $dark-fifth;

      &:not(:last-child) {
        margin-bottom: 8px;
      }
    }
  }

  .buttons {
    .icon-link-open,
    .icon-share {
      width: 16px;
      height: 16px;
      background-color: $dark-fifth;
    }

    &__desktop {
      display: flex;
      gap: 12px;

      .icon-link-open,
      .icon-share {
        margin-left: 4px;
      }
    }

    &__mobile {
      display: none;
      gap: 16px;
    }
  }

  &_activated {
    background-color: $light-third;

    .info__status {
      background-color: $light-fifth;
    }
  }

  &_new {
    .info__status {
      background-color: $services-fuel;
    }
  }
}

@media (max-width: 900px) {
  .certificates-item {
    padding: 12px 16px;

    .buttons {
      &__desktop {
        display: none;
      }

      &__mobile {
        display: inline-flex;
      }
    }
  }
}
</style>
